import { apiClient } from './api';
import {
  StoryboardStartRequest,
  StoryboardStartResponse,
  CreateSceneRequest,
  StoryboardSceneResponse,
  AddCameraDirectionRequest,
  AddCharacterAnimationRequest,
  AddTimingRequest,
  CreateShotsRequest,
  StoryboardShotsResponse,
  StoryboardExportResponse,
  StoryboardSession,
  StoryboardDetails,
} from '../models/storyboard';

const SESSION_STORAGE_KEY = 'storyboardSessions';

export class StoryboardService {
  // API calls
  static async startSession(request: StoryboardStartRequest): Promise<StoryboardStartResponse> {
    const response = await apiClient.post('/api/storyboard/session/start', request);
    return response.data;
  }

  static async createScene(sessionId: string, request: CreateSceneRequest): Promise<StoryboardSceneResponse> {
    const response = await apiClient.post(`/api/storyboard/session/${sessionId}/create-scene`, request);
    return response.data;
  }

  static async addCameraDirection(sessionId: string, request: AddCameraDirectionRequest): Promise<StoryboardSceneResponse> {
    const response = await apiClient.post(`/api/storyboard/session/${sessionId}/add-camera-direction`, request);
    return response.data;
  }

  static async addCharacterAnimation(sessionId: string, request: AddCharacterAnimationRequest): Promise<StoryboardSceneResponse> {
    const response = await apiClient.post(`/api/storyboard/session/${sessionId}/add-character-animation`, request);
    return response.data;
  }

  static async addTiming(sessionId: string, request: AddTimingRequest): Promise<StoryboardSceneResponse> {
    const response = await apiClient.post(`/api/storyboard/session/${sessionId}/add-timing`, request);
    return response.data;
  }

  static async createShots(sessionId: string, request: CreateShotsRequest): Promise<StoryboardShotsResponse> {
    const response = await apiClient.post(`/api/storyboard/session/${sessionId}/create-shots`, request);
    return response.data;
  }

  static async exportStoryboard(sessionId: string): Promise<StoryboardExportResponse> {
    const response = await apiClient.get(`/api/storyboard/session/${sessionId}/export`);
    return response.data;
  }

  static async fetchSessionFromApi(sessionId: string): Promise<StoryboardDetails> {
    // Use the export endpoint as recommended in the backend documentation
    const response = await apiClient.get(`/api/storyboard/session/${sessionId}/export`);
    
    // The export endpoint returns: {sessionId, export: {...}, success, errors}
    // We need to extract the 'export' property which contains the actual storyboard data
    if (response.data.success && response.data.export) {
      return response.data.export;
    } else {
      throw new Error(`Failed to fetch storyboard: ${response.data.errors?.join(', ') || 'Unknown error'}`);
    }
  }

  // Local session management
  static createLocalSession(apiSessionId: string, storyboard: StoryboardDetails): StoryboardSession {
    const session: StoryboardSession = {
      id: `storyboard-${Date.now()}`,
      sessionId: apiSessionId,
      storyboard,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    this.saveSession(session);
    return session;
  }

  static getSession(id: string): StoryboardSession | null {
    const sessions = this.getAllSessions();
    return sessions.find(s => s.id === id) || null;
  }

  static getAllSessions(): StoryboardSession[] {
    const stored = localStorage.getItem(SESSION_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  }

  static saveSession(session: StoryboardSession): void {
    const sessions = this.getAllSessions();
    const index = sessions.findIndex(s => s.id === session.id);
    
    if (index >= 0) {
      sessions[index] = session;
    } else {
      sessions.push(session);
    }

    session.updatedAt = new Date().toISOString();
    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(sessions));
  }

  static updateStoryboard(sessionId: string, storyboard: StoryboardDetails): void {
    const session = this.getSession(sessionId);
    if (session) {
      session.storyboard = storyboard;
      this.saveSession(session);
    }
  }

  static deleteSession(id: string): void {
    const sessions = this.getAllSessions();
    const filtered = sessions.filter(s => s.id !== id);
    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(filtered));
  }

  static exportToMarkdown(storyboard: StoryboardDetails): string {
    let content = `# Storyboard: ${storyboard.title}\n\n`;
    
    content += `## Overview\n\n`;
    content += `- **Genre:** ${storyboard.genre}\n`;
    content += `- **Style:** ${storyboard.style}\n`;
    content += `- **Aspect Ratio:** ${storyboard.aspectRatio}\n`;
    if (storyboard.estimatedDuration) {
      content += `- **Estimated Duration:** ${storyboard.estimatedDuration}\n`;
    }
    content += `- **Total Scenes:** ${storyboard.scenes.length}\n`;
    if (storyboard.totalShots) {
      content += `- **Total Shots:** ${storyboard.totalShots}\n`;
    }
    content += '\n';

    content += `## Concept\n\n${storyboard.concept}\n\n`;

    // Scenes
    storyboard.scenes.forEach((scene) => {
      content += `## Scene ${scene.sceneNumber}: ${scene.title}\n\n`;
      
      content += `### Scene Details\n`;
      content += `- **Location:** ${scene.location}\n`;
      content += `- **Time of Day:** ${scene.timeOfDay}\n`;
      content += `- **Mood:** ${scene.mood}\n`;
      content += `- **Visual Style:** ${scene.visualStyle}\n`;
      if (scene.duration) {
        content += `- **Duration:** ${scene.duration} seconds\n`;
      }
      content += '\n';

      content += `### Description\n${scene.description}\n\n`;

      if (scene.characters.length > 0) {
        content += `### Characters\n`;
        scene.characters.forEach(char => content += `- ${char}\n`);
        content += '\n';
      }

      if (scene.cameraDirections?.length) {
        content += `### Camera Directions\n`;
        scene.cameraDirections.forEach(dir => content += `- ${dir}\n`);
        content += '\n';
      }

      if (scene.characterAnimations?.length) {
        content += `### Character Animations\n`;
        scene.characterAnimations.forEach(anim => {
          content += `- **${anim.characterName}:** ${anim.animation}`;
          if (anim.timing) content += ` (${anim.timing})`;
          content += '\n';
        });
        content += '\n';
      }

      if (scene.timing) {
        content += `### Timing\n`;
        content += `- **Duration:** ${scene.timing.duration} seconds\n`;
        content += `- **Pacing:** ${scene.timing.pacing}\n`;
        if (scene.timing.beatPoints?.length) {
          content += `- **Beat Points:**\n`;
          scene.timing.beatPoints.forEach(beat => content += `  - ${beat}\n`);
        }
        content += '\n';
      }

      if (scene.shots?.length) {
        content += `### Shots\n\n`;
        scene.shots.forEach(shot => {
          content += `#### Shot ${shot.shotNumber}\n`;
          content += `- **Type:** ${shot.shotType}\n`;
          content += `- **Framing:** ${shot.framing}\n`;
          if (shot.angle) content += `- **Angle:** ${shot.angle}\n`;
          if (shot.cameraMovement) content += `- **Camera Movement:** ${shot.cameraMovement}\n`;
          content += `- **Duration:** ${shot.duration} seconds\n`;
          content += `\n**Description:** ${shot.description}\n`;
          content += `\n**Action:** ${shot.action}\n`;
          if (shot.dialogue) content += `\n**Dialogue:** "${shot.dialogue}"\n`;
          if (shot.transition) content += `\n**Transition:** ${shot.transition}\n`;
          if (shot.notes) content += `\n**Notes:** ${shot.notes}\n`;
          content += '\n';
        });
      }

      if (scene.notes) {
        content += `### Notes\n${scene.notes}\n\n`;
      }

      content += '---\n\n';
    });

    return content;
  }
}